import type { ReactNode } from "react";

import { useCasePlan } from "@/api";
import { MEASURE_IMPLAUSIBLE, type DemoFinding } from "@/lib/outline";

import styles from "./DisputedFigureNotice.module.css";
import { StateMessage } from "./StateMessage";

/**
 * The walls whose figure the sketch contradicts, as the Vorgang's plan answer
 * carries them (`GET …/plan`, the cached query the board reads) — in the
 * board's numbering, which is what the server's sentence already speaks.
 *
 * Quiet by design: while the plan is on its way, where it failed, and for a
 * `null` Vorgang the list is empty. A notice that only repeats what the board
 * says must never hold the gesture it stands beside.
 */
export function useDisputedFindings(caseId: string | null): DemoFinding[] {
  const plan = useCasePlan(caseId);
  return (plan.data?.outlineFindings ?? []).filter((finding) => finding.rule === MEASURE_IMPLAUSIBLE);
}

interface DisputedFigureNoticeProps {
  title: string;
  findings: DemoFinding[];
  /** What the shape editor does with such a wall, and where it is settled. */
  closing: string;
  /** The way to the board. */
  action?: ReactNode;
}

/**
 * „Form ändern …“ draws a disputed wall at its **typed** figure, not at the
 * sketch's length as the board does (E-64, Ruling R-19: not redrawn, said).
 * Told before the dialog is answered and inside the editor, with the server's
 * own sentence per wall. `strong`: it asks to be answered — on the board.
 */
export function DisputedFigureNotice({ title, findings, closing, action }: DisputedFigureNoticeProps) {
  return (
    <StateMessage strong title={title} action={action}>
      <ul className={styles.lines}>
        {findings.map((finding, index) => (
          <li key={`${finding.wallIndex ?? "-"}-${index}`}>{finding.message}</li>
        ))}
      </ul>
      <p className={styles.closing}>{closing}</p>
    </StateMessage>
  );
}
